import { Panel } from "../../../components/panels/Panel";
import { DateRangePickerComponent } from "../../../components/others/DateRangePickerComponent";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { ChevronRightIcon, ClockIcon, InboxIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import {
  formatSecondsToFullDateTime,
  formatSecondsToMinutesSeconds
} from "../../../helpers/date/date-helper";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import Badge from "../../../components/badges/Badge";
import {
  findPoolByRealId,
  getSchemeId
} from "../../../services/live-interface/dior/dior-interface-service";
import ToggleSwitch from "../../../components/toggle/ToggleSwitch";

export const DiorDashboardPage = () => {

  /* Dates management */
  const now = new Date();
  const defaultStart = new Date(now);
  defaultStart.setHours(0, 0, 0, 0);
  const defaultEnd = new Date(now);
  defaultEnd.setHours(23, 59, 0, 0);

  const [selectedRange, setSelectedRange] = useState({ start: defaultStart, end: defaultEnd });

  const handleRangeSelection = (start, end) => {
    setSelectedRange({ start, end });
    setRacksList([]);
    setSelectedRackHistory(null);

    updateData(Math.floor(start / 1000), Math.floor(end / 1000));
  };

  /* Racks list management */
  const [racksData, setRacksData] = useState([]);
  const [racksList, setRacksList] = useState([]);
  const [selectedRackHistory, setSelectedRackHistory] = useState(null);
  const [showAguasPools, setShowAguasPools] = useState(false);

  const updateData = async (start, end) => {
    const newRacksData = await getInfluxdbData(
      'engine',
      'dives',
      [],
      ['in_timestamp_ms', 'duration'],
      start,
      end,
      '1ms',
      null,
      null,
      [],
      null,
      null,
      false,
      'desc',
    );

    setRacksData(newRacksData);
    setRacksList(createRacksList(newRacksData));
  };

  const createRacksList = (rawData) => {
    const seen = new Set();

    return [...rawData]
      .sort((a, b) => a.in_timestamp_ms - b.in_timestamp_ms)
      .filter(item => {
        if (seen.has(item.rack_id)) return false;
        seen.add(item.rack_id);
        return true;
      })
      .map(item => ({
        rack_id: item.rack_id,
        type: item.type,
        time_s: Math.floor(item.in_timestamp_ms / 1000),
      }));
  };

  const handleRackSelection = (rackType, rackId) => {
    setSelectedRackHistory(createRackItemsList(rackType, rackId));
  };

  const createRackItemsList = (rackType, rackId) => {
    return [...racksData]
      .filter(item => item.type === rackType && item.rack_id === rackId)
      .sort((a, b) => a.in_timestamp_ms - b.in_timestamp_ms)
      .map(item => ({
        ...item,
        time_s: Math.floor(item.in_timestamp_ms / 1000),
      }));
  };

  return (
    <Panel category='main' title='DASHBOARD'>
      <div className="h-full flex flex-col gap-4">
        <Panel noHeader category="secondary" rounded height="h-16">
          <div className="flex gap-8 items-center h-full">
            <div className="flex gap-2 items-center">
              <div>Start date</div>
              <div className="bg-perception-black-800 rounded-md px-2 py-1">
                {formatSecondsToFullDateTime(selectedRange.start.getTime() / 1000, false)}
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <div>End date</div>
              <div className="bg-perception-black-800 rounded-md px-2 py-1">
                {formatSecondsToFullDateTime(selectedRange.end.getTime() / 1000, false)}
              </div>
            </div>

            <DateRangePickerComponent
              trigger={({ isCalendarOpen, setIsCalendarOpen }) => (
                <ActionButton text="Select date range" Icon={ClockIcon} size="sm" onClick={() => setIsCalendarOpen(!isCalendarOpen)} />
              )}
              defaultStart={defaultStart} defaultEnd={defaultEnd}
              callback={handleRangeSelection}
              cancelText="Cancel" validateText="Load racks"
            />

            <div className="flex gap-2 items-center">
              Show "AGUAS" pools
              <ToggleSwitch enabled={showAguasPools} setEnabled={setShowAguasPools} storageToggleId="show_aguas_pool"/>
            </div>
          </div>
        </Panel>
        <div className="h-full flex-1 flex gap-4 min-h-0">
          <Panel title="RACKS LIST" rounded>
            <div className="flex flex-col gap-4 h-full">
              {
                racksList.map((rack, rackIndex) => (
                  <div className={`flex bg-perception-black-700 rounded-md p-4 gap-4 items-center relative group cursor-pointer
                    border border-2 ${selectedRackHistory?.[0]?.rack_id === rack.rack_id ? 'border-perception-blue' : 'border-transparent'}`}
                    onClick={() => handleRackSelection(rack.type, rack.rack_id)} key={rackIndex}
                  >
                    <Badge text={rack.type} type="blue"/>
                    <div>{formatSecondsToFullDateTime(rack.time_s, false)}</div>
                    <div className="text-perception-gray-600 text-sm">#{rack.rack_id}</div>

                    <div className="absolute right-4 group-hover:right-2 transition-all top-1/2 -translate-y-1/2">
                      <ChevronRightIcon className="size-8 text-perception-gray-600"/>
                    </div>
                  </div>
                ))
              }
              {
                racksList.length === 0 &&
                  <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                    <InboxIcon className="size-32 text-perception-gray-700"/>
                    <div className="text-perception-gray-700 text-2xl">Please select a date range</div>
                  </div>
              }
            </div>
          </Panel>
          <Panel title="RACK DETAILS" rounded>
            {
              selectedRackHistory ?
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4 items-center">
                    <Badge text={selectedRackHistory[0].type} type="blue"/>
                    <div>{formatSecondsToFullDateTime(selectedRackHistory[0].time_s, false)}</div>
                    <div className="text-perception-gray-600 text-sm">#{selectedRackHistory[0].rack_id}</div>
                  </div>
                  {
                    selectedRackHistory
                      .filter((rackStep) => showAguasPools || !findPoolByRealId(rackStep.pool_id)?.name.includes('AGUAS'))
                      .map((rackStep, rackStepIndex) => (
                      <div className="flex bg-perception-black-700 rounded-md px-4 py-2 gap-4 items-center relative" key={rackStepIndex}>
                        <div className="text-xs text-perception-gray-600">#{rackStepIndex + 1}</div>
                        <Badge text={formatSecondsToFullDateTime(rackStep.time_s, false)}/>
                        <div className="flex gap-2">
                          <Badge text={getSchemeId(rackStep.pool_id)} type="blue"/>
                          {findPoolByRealId(rackStep.pool_id)?.name}
                        </div>
                        <div className="bg-perception-black-500 px-2 py-0.5 rounded-md text-sm">{formatSecondsToMinutesSeconds(rackStep.duration)}</div>
                      </div>
                    ))
                  }
                </div>
              :
                <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
                  <InboxIcon className="size-32 text-perception-gray-700"/>
                  <div className="text-perception-gray-700 text-2xl">No rack selected</div>
                </div>
            }
          </Panel>
        </div>
      </div>
    </Panel>
  );
};